import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

/**
 * Build a smooth polyline for a sparkline given numeric samples.
 * Values are normalized inside a 116×34 viewport (matches the mockup).
 */
function sparkPoints(values, width = 116, height = 34) {
    if (!values || !values.length) return { line: '', dot: null };
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = Math.max(max - min, 1);
    const step = values.length > 1 ? width / (values.length - 1) : 0;
    const pts = values.map((v, i) => {
        const x = i * step;
        const y = height - ((v - min) / range) * height;
        return [x, y];
    });
    return {
        line: pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' '),
        dot: pts[pts.length - 1],
    };
}

/**
 * KpiCard — canonical stat tile for R2.
 *
 * @param {Object} props
 * @param {string} props.label
 * @param {React.ReactNode} props.value
 * @param {React.ReactNode} [props.subtitle]
 * @param {string|number|null} [props.delta] - e.g. '+12%' or a number
 * @param {'up'|'down'|null} [props.trend]
 * @param {number[]} [props.spark] - Numeric samples for the trailing sparkline
 * @param {string} [props.testId]
 */
export default function KpiCard({ label, value, subtitle, delta, trend, spark, testId }) {
    const deltaBg = trend === 'down' ? 'var(--bad-bg)' : 'var(--good-bg)';
    const deltaFg = trend === 'down' ? 'var(--bad)' : 'var(--good)';
    const DeltaIcon = trend === 'down' ? ArrowDownRight : ArrowUpRight;
    const showSpark = Array.isArray(spark) && spark.length > 1;
    const { line, dot } = sparkPoints(spark || []);

    return (
        <div
            className="rounded-[14px] p-4 sm:p-5 flex flex-col"
            style={{
                background: 'var(--surface)',
                border: '1px solid var(--line)',
                color: 'var(--ink-1)',
                minHeight: 108,
            }}
            data-testid={testId}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p
                        className="uppercase tracking-wider truncate"
                        style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: 'var(--ink-label)',
                            letterSpacing: '0.08em',
                        }}
                    >
                        {label}
                    </p>
                    <p
                        className="mt-1"
                        style={{
                            fontFamily: 'Manrope, system-ui, sans-serif',
                            fontSize: 24,
                            fontWeight: 800,
                            color: 'var(--ink-1)',
                            fontVariantNumeric: 'tabular-nums',
                            lineHeight: 1.1,
                        }}
                    >
                        {value}
                    </p>
                    {subtitle && (
                        <p style={{ fontSize: 11.5, color: 'var(--ink-label)', marginTop: 4 }}>
                            {subtitle}
                        </p>
                    )}
                </div>
                {delta != null && (
                    <span
                        className="inline-flex items-center gap-1 rounded-full text-[11px] font-semibold"
                        style={{ background: deltaBg, color: deltaFg, padding: '2px 8px' }}
                    >
                        <DeltaIcon className="w-3 h-3" />
                        {delta}
                    </span>
                )}
            </div>
            {showSpark && (
                <svg
                    className="mt-3"
                    width="116"
                    height="34"
                    viewBox="0 0 116 34"
                    role="presentation"
                    aria-hidden="true"
                >
                    <polyline
                        fill="none"
                        stroke="var(--ink-3)"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        points={line}
                    />
                    {dot && (
                        <circle cx={dot[0]} cy={dot[1]} r="2.4" fill="var(--ws, var(--ink-1))" />
                    )}
                </svg>
            )}
        </div>
    );
}
